import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { getBasePrice, getBillingInfo } from "../../../../api/Billing";
import { billingAtom } from "../../../../atoms/BillingAtom";
import { SettingsHelpVideosAtom } from "../../../../atoms/SettingsHelpVideos";
import { STRIPE_PUBLIC_KEY } from "../../../../config";
import {
  HelpVideoNameMap,
  IHelpContexts,
} from "../../../../types/HelpVideosTypes";
import Loading from "../../../Loading";
import { useModal } from "../../Modal";
import Payment from "./Payment";
import PreBuy from "./PreBuy";
import UpdatePayment from "./UpdateModal/UpdatePayment";

interface IProps {
  onOkClicked: () => void;
  onCancelClicked: () => void;
  isModal?: boolean;
  updateSubscription?: boolean;
  customTitle?: string;
}

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

export default function StripeProvider(props: IProps): JSX.Element {
  const {
    onOkClicked,
    onCancelClicked,
    isModal,
    updateSubscription,
    customTitle,
  } = props;

  const [billingInfo, setBillingInfo] = useRecoilState(billingAtom);
  const [loading, setLoading] = useState(true);
  const modal = useModal();

  const [, setHelpVideo] = useRecoilState(SettingsHelpVideosAtom);

  useEffect(() => {
    setBillingInfo((oldBillingInfo) => ({ ...oldBillingInfo, update: true }));
    setHelpVideo({
      helpTitle: HelpVideoNameMap[IHelpContexts.SettingsBilling],
      helpContext: IHelpContexts.SettingsBilling,
    });
  }, []);

  useEffect(() => {
    (async () => {
      const requestPrice = await getBasePrice();
      setBillingInfo((prevState) => ({
        ...prevState,
        ...requestPrice,
      }));
    })();
  }, []);

  useEffect(() => {
    if (!billingInfo.update) return;
    setLoading(true);
    (async () => {
      const newBilling = await getBillingInfo();
      setBillingInfo((oldBilling) => ({
        ...oldBilling,
        ...newBilling,
        initialAdmins: newBilling.admins || 0,
        initialExternalUsers: newBilling.externalUsers || 0,
        update: false,
      }));
      setLoading(false);
      if (!newBilling?.active)
        modal.pushModal(
          <PreBuy
            onAgree={() => modal.popModal()}
            onCancel={() => {
              modal.popModal();
              onCancelClicked();
            }}
          />,
          "",
          true
        );
    })();
  }, [billingInfo.update]);

  return (
    <Elements stripe={stripePromise}>
      {loading ? (
        <Loading />
      ) : billingInfo.active ? (
        <UpdatePayment
          onCancelClicked={onCancelClicked}
          onOkClicked={onOkClicked}
        />
      ) : (
        <Payment
          isModal={isModal}
          onCancelClicked={onCancelClicked}
          onOkClicked={onOkClicked}
        />
      )}
    </Elements>
  );
}
